/**
 * Timeout policy for `callGraphQLEndpoint`.
 *
 * Kept in its own dependency-free module so it can be exercised by
 * `scripts/test-eddm-route-walk.mjs` (this repo's jest cannot start — see that
 * file's header) without dragging in `config/config` and `module/auth`.
 */

/** Fallback when nothing is configured. See BUILT_IN_DEFAULT_TIMEOUT_MS notes below. */
export const BUILT_IN_DEFAULT_TIMEOUT_MS = 60000;

/**
 * Parse the `REACT_APP_GRAPHQL_TIMEOUT_MS` escape hatch.
 *
 * `Number(raw) || fallback` was wrong: 0 is falsy, so the documented
 * "0 disables the timeout" escape hatch silently fell back to the default and
 * there was no way to turn the timeout off through the env var at all. Only an
 * absent/blank/unparseable/negative value falls back now; 0 is honoured and
 * means "no timeout".
 *
 * @param {string|number|undefined|null} raw
 * @param {number} [fallback]
 * @returns {number} milliseconds; 0 means the timeout is disabled.
 */
export const resolveGraphQLTimeoutMs = (raw, fallback = BUILT_IN_DEFAULT_TIMEOUT_MS) => {
  if (raw === undefined || raw === null || String(raw).trim() === "") {
    return fallback;
  }
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return fallback;
  }
  return parsed;
};

export const GRAPHQL_TIMEOUT_ERROR_NAME = "GraphQLTimeoutError";

/**
 * A caller-driven cancel, as distinct from a deadline being hit.
 *
 * `callGraphQLEndpoint` used to convert an externally supplied AbortSignal into
 * `createGraphQLTimeoutError`, so a user pressing Cancel surfaced to the caller
 * — and therefore to the screen — as "it timed out". The two are different
 * events with different copy and different recovery, so they get different
 * error names.
 */
export const GRAPHQL_ABORT_ERROR_NAME = "GraphQLAbortError";

export const createGraphQLTimeoutError = (ms) => {
  const error = new Error(`GraphQL request timed out after ${ms}ms`);
  error.name = GRAPHQL_TIMEOUT_ERROR_NAME;
  return error;
};

export const createGraphQLAbortError = () => {
  const error = new Error("GraphQL request was cancelled");
  error.name = GRAPHQL_ABORT_ERROR_NAME;
  return error;
};

export const isGraphQLAbortError = (error) => Boolean(error) && error.name === GRAPHQL_ABORT_ERROR_NAME;

/**
 * A cancel is deliberately NOT a timeout here: callers branch on this to decide
 * whether to show "taking longer than expected", and a cancel must never do so.
 */
export const isGraphQLTimeoutError = (error) =>
  Boolean(error) &&
  !isGraphQLAbortError(error) &&
  (error.name === GRAPHQL_TIMEOUT_ERROR_NAME || error.name === "AbortError");
